import { ExternalLink, Paperclip } from "lucide-react";
import BaseDrawer from "@/shared/components/Drawer";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import {
  CREDIT_LOCKED_HINT,
  CREDIT_UNITS_LOCKED_HINT,
  DEBIT_LOCKED_HINT,
  DEBIT_UNITS_LOCKED_HINT,
  LOCKED_AMOUNT_INPUT_CLASS,
  formatAmount,
  formatCurrencyField,
  getAmountSideLock,
  getAllocationCategory,
  getAllocationCategoryEmoji,
  normalizeCurrencyTyping,
} from "./utils";
import type { EditableAllocationState } from "./utils";
import type { LedgerAllocation } from "../../types/accounting.types";

type LegacyActivityDetailProps = {
  open: boolean
  onClose: () => void
  selectedAllocation: LedgerAllocation | null
  simpleEditorRow: EditableAllocationState | null
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isSimpleEditorBusy: boolean
  onSave: () => void
  onOpenSettlement?: (settlementId: string) => void
  readOnly?: boolean
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }
  catch {
    return iso;
  }
};

const getDrawerTitle = (allocation: LedgerAllocation | null): string => {
  if (!allocation) return "Écriture legacy";
  const cat = getAllocationCategory(allocation);
  const emoji = getAllocationCategoryEmoji(cat);
  const opId = allocation.activityOperationId ? ` — OP${allocation.activityOperationId}` : "";
  switch (cat) {
    case "legacy_purchase":
      return `${emoji} Achats stockés${opId}`;
    case "legacy_travel":
      return `${emoji} Voyages & Loisirs${opId}`;
    case "legacy_reimbursement":
      return `${emoji} Remboursement v1${opId}`;
    case "legacy_rental":
      return `${emoji} Locations${opId}`;
    default:
      return `Écriture legacy${opId}`;
  }
};

const LegacyActivityDetail = ({
  open,
  onClose,
  selectedAllocation,
  simpleEditorRow,
  setField,
  isSimpleEditorBusy,
  onSave,
  onOpenSettlement,
  readOnly,
}: LegacyActivityDetailProps) => {
  const { isDebitLocked, isCreditLocked } = getAmountSideLock(simpleEditorRow?.debit, simpleEditorRow?.credit);
  const { isDebitLocked: isUnitsDebitLocked, isCreditLocked: isUnitsCreditLocked } = getAmountSideLock(simpleEditorRow?.stockDebitQuantity, simpleEditorRow?.stockCreditQuantity);
  return (
    <BaseDrawer
      drawerId="DRW-LEGACY"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      isFirstLevel={false}
      onBack={onClose}
      width="893px"
      zIndex={65}
      bodyClassName="[&_*]:select-text"
      title={getDrawerTitle(selectedAllocation)}
      description="Affectation liée à une opération d'activité (legacy)."
    >
      {/* Activity info section */}
      {selectedAllocation && (
        <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3 mb-4 space-y-3">
          <div className="text-[12px] font-medium text-sky-900">Informations activité</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            {selectedAllocation.activityOperationCategory && (
              <div>
                <span className="text-sky-700">Catégorie :</span>
                {" "}
                <span className="font-medium text-sky-950">{selectedAllocation.activityOperationCategory}</span>
              </div>
            )}
            {selectedAllocation.activityOperationId && (
              <div>
                <span className="text-sky-700">ID opération :</span>
                {" "}
                <span className="font-medium text-sky-950">
                  OP
                  {selectedAllocation.activityOperationId}
                </span>
              </div>
            )}
            {selectedAllocation.beneficiaryName && (
              <div>
                <span className="text-sky-700">Bénéficiaire :</span>
                {" "}
                <span className="font-medium text-sky-950">{selectedAllocation.beneficiaryName}</span>
              </div>
            )}
            {selectedAllocation.activityOperationLabel && (
              <div className="md:col-span-2">
                <span className="text-sky-700">Libellé opération :</span>
                {" "}
                <span className="font-medium text-sky-950">{selectedAllocation.activityOperationLabel}</span>
              </div>
            )}
            {selectedAllocation.activityOperationStatus && (
              <div>
                <span className="text-sky-700">Statut :</span>
                {" "}
                <span className="inline-flex items-center rounded-full bg-sky-200/70 px-2 py-0.5 text-[11px] font-medium text-sky-900 ml-1">
                  {selectedAllocation.activityOperationStatus}
                </span>
              </div>
            )}
            {(selectedAllocation.activityOperationDebit !== null || selectedAllocation.activityOperationCredit !== null) && (
              <div>
                <span className="text-sky-700">Montant opération :</span>
                {" "}
                <span className="font-medium tabular-nums text-sky-950">
                  {selectedAllocation.activityOperationDebit ? `${formatAmount(selectedAllocation.activityOperationDebit)} € (débit)` : ""}
                  {selectedAllocation.activityOperationDebit && selectedAllocation.activityOperationCredit ? " / " : ""}
                  {selectedAllocation.activityOperationCredit ? `${formatAmount(selectedAllocation.activityOperationCredit)} € (crédit)` : ""}
                </span>
              </div>
            )}
            {selectedAllocation.activityOperationClientName && (
              <div>
                <span className="text-sky-700">Bénéficiaire :</span>
                {" "}
                <span className="font-medium text-sky-950">
                  {selectedAllocation.activityOperationClientName}
                  {selectedAllocation.activityOperationClientSite ? ` — ${selectedAllocation.activityOperationClientSite}` : ""}
                </span>
              </div>
            )}
          </div>

          {selectedAllocation.activityOperationAttachments.length > 0 && (
            <div className="border-t border-sky-200 pt-2 space-y-1">
              <div className="text-[11px] font-medium text-sky-800 flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
                Pièces jointes
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedAllocation.activityOperationAttachments.map((att) => (
                  <a
                    key={att.url}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded border border-sky-200 bg-white px-2 py-1 text-[11px] text-sky-700 hover:bg-sky-100 hover:text-sky-900 transition-colors"
                  >
                    <Paperclip className="h-3 w-3 shrink-0" />
                    {att.fileName || att.label}
                    <ExternalLink className="h-3 w-3 shrink-0 opacity-50" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settlement info section */}
      {selectedAllocation?.settlementId && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 mb-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-medium text-indigo-900">Règlement associé</div>
            {onOpenSettlement && (
              <button
                type="button"
                onClick={() => onOpenSettlement(selectedAllocation.settlementId!)}
                className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
              >
                Voir le règlement
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            {selectedAllocation.settlementNumber && (
              <div>
                <span className="text-indigo-700">N° :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementNumber}</span>
              </div>
            )}
            {selectedAllocation.settlementDate && (
              <div>
                <span className="text-indigo-700">Date :</span>
                {" "}
                <span className="font-medium text-indigo-950">{formatDate(selectedAllocation.settlementDate)}</span>
              </div>
            )}
            {selectedAllocation.settlementIssuer && (
              <div>
                <span className="text-indigo-700">Émetteur :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementIssuer}</span>
              </div>
            )}
            {selectedAllocation.settlementPaymentMethod && (
              <div>
                <span className="text-indigo-700">Support :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementPaymentMethod}</span>
              </div>
            )}
            {selectedAllocation.settlementDeferredDate && (
              <div>
                <span className="text-indigo-700">Date différée :</span>
                {" "}
                <span className="font-medium text-indigo-950">{formatDate(selectedAllocation.settlementDeferredDate)}</span>
              </div>
            )}
          </div>
          {(selectedAllocation.settlementAmount !== null || selectedAllocation.settlementRemainingAmount !== null) && (
            <div className="border-t border-indigo-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.settlementAmount !== null && (
                <div>
                  <span className="text-indigo-700">Montant :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-indigo-950">
                    {formatAmount(selectedAllocation.settlementAmount)}
                    {" "}
                    &euro;
                  </span>
                </div>
              )}
              {selectedAllocation.settlementRemainingAmount !== null && (
                <div>
                  <span className="text-indigo-700">Solde :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-indigo-950">
                    {formatAmount(selectedAllocation.settlementRemainingAmount)}
                    {" "}
                    &euro;
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inline editor */}
      {selectedAllocation && !readOnly && (
        <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-2 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight">
          <div className="text-[11px] font-medium text-slate-700">Écriture en cours d&apos;édition</div>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600">Date</label>
              <Input
                type="date"
                min={ACCOUNTING_DATE_BOUNDS.min}
                max={ACCOUNTING_DATE_BOUNDS.max}
                value={simpleEditorRow?.date ?? ""}
                onChange={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "date", event.target.value, selectedAllocation);
                }}
                className="h-7 !text-[12px] md:!text-[12px] leading-tight"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600">Type budget</label>
              <select
                className="h-7 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
                value={simpleEditorRow?.budgetType || "ASC"}
                onChange={(event) => {
                  if (!selectedAllocation) return;
                  setField(selectedAllocation.id, "budgetType", event.target.value, selectedAllocation);
                }}
                disabled
              >
                <option value="ASC">ASC</option>
                <option value="AEP">AEP</option>
                <option value="STOCK">STOCK</option>
              </select>
            </div>
            {(simpleEditorRow?.budgetType ?? "").toUpperCase() === "STOCK"
              ? (
                <>
                  <div className="space-y-1">
                    <label className="text-[12px] leading-tight text-slate-600">Débit (unités)</label>
                    <Input
                      inputMode="decimal"
                      value={simpleEditorRow?.stockDebitQuantity ?? ""}
                      onChange={(event) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "stockDebitQuantity", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                      }}
                      className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isUnitsDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                      placeholder="0"
                      readOnly={isUnitsDebitLocked}
                      aria-disabled={isUnitsDebitLocked}
                      title={isUnitsDebitLocked ? DEBIT_UNITS_LOCKED_HINT : undefined}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[12px] leading-tight text-slate-600">Crédit (unités)</label>
                    <Input
                      inputMode="decimal"
                      value={simpleEditorRow?.stockCreditQuantity ?? ""}
                      onChange={(event) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "stockCreditQuantity", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                      }}
                      className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isUnitsCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                      placeholder="0"
                      readOnly={isUnitsCreditLocked}
                      aria-disabled={isUnitsCreditLocked}
                      title={isUnitsCreditLocked ? CREDIT_UNITS_LOCKED_HINT : undefined}
                    />
                  </div>
                </>
              )
              : (
                <>
                  <div className="space-y-1">
                    <label className="text-[12px] leading-tight text-slate-600">Débit</label>
                    <Input
                      inputMode="decimal"
                      value={simpleEditorRow?.debit ?? ""}
                      onChange={(event) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "debit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                      }}
                      onBlur={(event) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "debit", formatCurrencyField(event.target.value), selectedAllocation);
                      }}
                      className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                      placeholder="0,00"
                      readOnly={isDebitLocked}
                      aria-disabled={isDebitLocked}
                      title={isDebitLocked ? DEBIT_LOCKED_HINT : undefined}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[12px] leading-tight text-slate-600">Crédit</label>
                    <Input
                      inputMode="decimal"
                      value={simpleEditorRow?.credit ?? ""}
                      onChange={(event) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "credit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                      }}
                      onBlur={(event) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "credit", formatCurrencyField(event.target.value), selectedAllocation);
                      }}
                      className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                      placeholder="0,00"
                      readOnly={isCreditLocked}
                      aria-disabled={isCreditLocked}
                      title={isCreditLocked ? CREDIT_LOCKED_HINT : undefined}
                    />
                  </div>
                </>
              )}
          </div>
          <div className="space-y-1">
            <label className="text-[12px] leading-tight text-slate-600">Libellé</label>
            <Input
              value={simpleEditorRow?.description ?? ""}
              onChange={(event) => {
                if (!selectedAllocation) return;
                setField(selectedAllocation.id, "description", event.target.value, selectedAllocation);
              }}
              className="h-7 !text-[12px] md:!text-[12px] leading-tight"
            />
          </div>
          <div className="flex justify-end gap-2 pt-0.5">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSimpleEditorBusy}
              className="h-7 text-[11px] px-3"
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="success"
              onClick={onSave}
              disabled={isSimpleEditorBusy}
              className="h-7 text-[11px] px-3"
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </BaseDrawer>
  );
};

export default LegacyActivityDetail;
